import { useState, useEffect, useCallback } from "react";
import "@/App.css";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useToast } from "@/hooks/use-toast";
import { Toaster } from "@/components/ui/toaster";
import { FileText, Download, Trash2, Edit, Plus } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function LoginScreen({ onLogin }) {
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState('');

  const handleLogin = () => {
    if (password === 'karfo2026simge') {
      onLogin();
    } else {
      setAuthError('Hatalı şifre');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle>Karfo Servis Sistemi</CardTitle>
          <CardDescription>Devam etmek için şifrenizi girin</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="password">Şifre</Label>
            <Input id="password" type="password" value={password}
              onChange={e => setPassword(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleLogin()}
              placeholder="••••••••" className="mt-1" />
            {authError && <p className="text-red-500 text-sm mt-1">{authError}</p>}
          </div>
        </CardContent>
        <CardFooter>
          <Button className="w-full" onClick={handleLogin}>Giriş Yap</Button>
        </CardFooter>
      </Card>
    </div>
  );
}

function App() {
  const { toast } = useToast();
  const [authenticated, setAuthenticated] = useState(() => sessionStorage.getItem('karfo_auth') === 'true');
  const [proposals, setProposals] = useState([]);
  const [filteredProposals, setFilteredProposals] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [monthlyStats, setMonthlyStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [formData, setFormData] = useState({
    is_numarasi: "",
    seri_numarasi: "",
    model: "",
    musteri_sikayeti: "",
    degisecek_parcalar: "",
    ederini_asar: false,
    ek_aciklama: "",
    kaza_detayi: "",
    ingilizce_gozuksun: true,
    kaza_detayi_tr: "",
    kullanici_hatasi: false,
    toplam_tutar: "",
    cihaz_degisimi: false
  });
  const [editingId, setEditingId] = useState(null);
  const [activeTab, setActiveTab] = useState("create");
  const [currentPage, setCurrentPage] = useState(1);
  const PROPOSALS_PER_PAGE = 10;

  const fetchProposals = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/proposals`);
      const sorted = [...response.data].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setProposals(sorted);
      setFilteredProposals(sorted);
    } catch (error) {
      console.error("Teklifler yüklenemedi:", error);
      toast({
        title: "Hata",
        description: "Teklifler yüklenemedi",
        variant: "destructive"
      });
    }
  }, [toast]);

  // Tarihe göre filtreleme
  const handleDayClick = (day, month, year) => {
    const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    setSelectedDate({ day, month, year, dateStr });
    
    // O güne ait teklifleri filtrele
    const filtered = proposals.filter(proposal => {
      const proposalDate = new Date(proposal.created_at);
      const proposalDateStr = `${proposalDate.getFullYear()}-${String(proposalDate.getMonth() + 1).padStart(2, '0')}-${String(proposalDate.getDate()).padStart(2, '0')}`;
      return proposalDateStr === dateStr;
    });
    
    setFilteredProposals(filtered);
    setCurrentPage(1);
  };

  // Filtreyi temizle
  const clearFilter = () => {
    setSelectedDate(null);
    setFilteredProposals(proposals);
    setCurrentPage(1);
  };

  const fetchMonthlyStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/stats/monthly`);
      setMonthlyStats(response.data);
    } catch (error) {
      console.error("Aylık istatistikler yüklenemedi:", error);
      // İstatistik yüklenemezse sessizce devam et
    }
  }, []);

  useEffect(() => {
    fetchProposals();
    fetchMonthlyStats();
  }, [fetchProposals, fetchMonthlyStats]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
  };
  const handleIsSayfasindanAyristir = async () => {
    try {
      const text = await navigator.clipboard.readText();
      const isNumarasiMatch = text.match(/Form No\s*Seri Numara\s*\n\s*([A-Za-z0-9]+)/);
      const seriNumarasiMatch = text.match(/Seri No:\s*\n\s*([A-Za-z0-9]+)-*/);
      const modelMatch = text.match(/Ürün Adı:\s*\n\s*([^\n]+)/);
      const genelToplamMatch = text.match(/Genel Toplam:\s*([\d.,]+)/);
      const arizaYorumuMatch = text.match(/Arıza Detayı Yorumu\s*\n+\s*([\s\S]*?)\n\s*İşlem Tipi/);

      const updates = {};
      if (isNumarasiMatch) updates.is_numarasi = isNumarasiMatch[1].trim();
      if (seriNumarasiMatch) updates.seri_numarasi = seriNumarasiMatch[1].trim();
      if (modelMatch) updates.model = modelMatch[1].trim();
      if (genelToplamMatch) {
        const normalized = genelToplamMatch[1].replace(/\./g, '').replace(',', '.');
        updates.toplam_tutar = normalized;
      }
      if (arizaYorumuMatch) updates.degisecek_parcalar = arizaYorumuMatch[1].trim();

      if (Object.keys(updates).length === 0) {
        toast({
          title: "Ayrıştırılamadı",
          description: "Panodaki metinde beklenen alanlar bulunamadı.",
          variant: "destructive"
        });
        return;
      }

      setFormData(prev => ({ ...prev, ...updates }));
      toast({
        title: "Ayrıştırıldı",
        description: `${Object.keys(updates).length} alan dolduruldu.`
      });
    } catch (err) {
      toast({
        title: "Pano okunamadı",
        description: "Tarayıcı izni gerekebilir, tekrar deneyin.",
        variant: "destructive"
      });
    }
  };
  const formatKazaDetayi = (text) => {
    let t = text.replace(/\s+/g, ' ').trim();
    t = t.replace(/(\d{1,2})\.\s*(?=[A-Z])/g, '\n$1. ');
    t = t.replace(/(\d{1,2})\)\s*(?=[A-Z])/g, '\n$1) ');
    const keyPhrases = [
      'Conclusion:',
      'Therefore,',
      'The incident was caused by a pilot error.',
      'Please note'
    ];
    keyPhrases.forEach(p => { t = t.split(p).join('\n' + p); });
    t = t.replace(/^\n+/, '').replace(/\n{2,}/g, '\n');
    return t;
  };

  const handleKazaDetayiPaste = (e) => {
    const pasted = e.clipboardData.getData('text');
    const isDjiReport = /analysis result|BAT SN:|MC SN:/i.test(pasted);
    if (isDjiReport) {
      e.preventDefault();
      const formatted = formatKazaDetayi(pasted);
      setFormData(prev => ({
        ...prev,
        kaza_detayi: prev.kaza_detayi ? prev.kaza_detayi + '\n' + formatted : formatted
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const data = {
        ...formData,
        toplam_tutar: parseFloat(formData.toplam_tutar)
      };

      if (editingId) {
        await axios.put(`${API}/proposals/${editingId}`, data);
        toast({
          title: "Başarılı!",
          description: "Teklif güncellendi"
        });
        setEditingId(null);
      } else {
        await axios.post(`${API}/proposals`, data);
        toast({
          title: "Başarılı!",
          description: "Teklif oluşturuldu"
        });
      }

      setFormData({
        is_numarasi: "",
        seri_numarasi: "",
        model: "",
        musteri_sikayeti: "",
        degisecek_parcalar: "",
        ederini_asar: false,
        ek_aciklama: "",
        kaza_detayi: "",
        ingilizce_gozuksun: true,
        kaza_detayi_tr: "",
        kullanici_hatasi: false,
        toplam_tutar: ""
      });
      fetchProposals();
      fetchMonthlyStats();
    } catch (error) {
      console.error("Hata:", error);
      toast({
        title: "Hata",
        description: "Teklif kaydedilemedi",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async (proposalId, isNumarasi) => {
    try {
      const response = await axios.get(`${API}/proposals/${proposalId}/pdf`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Teklif_${isNumarasi}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast({
        title: "Başarılı!",
        description: "PDF indirildi"
      });
    } catch (error) {
      console.error("PDF indirilemedi:", error);
      toast({
        title: "Hata",
        description: "PDF indirilemedi",
        variant: "destructive"
      });
    }
  };

  const handleEdit = (proposal) => {
    setFormData({
      is_numarasi: proposal.is_numarasi,
      seri_numarasi: proposal.seri_numarasi,
      model: proposal.model,
      musteri_sikayeti: proposal.musteri_sikayeti || "",
      degisecek_parcalar: proposal.degisecek_parcalar,
      ederini_asar: proposal.ederini_asar || false,
      ek_aciklama: proposal.ek_aciklama || "",
      kaza_detayi: proposal.kaza_detayi || "",
      ingilizce_gozuksun: proposal.ingilizce_gozuksun !== undefined ? proposal.ingilizce_gozuksun : true,
      kaza_detayi_tr: proposal.kaza_detayi_tr || "",
      kullanici_hatasi: proposal.kullanici_hatasi || false,
      toplam_tutar: proposal.toplam_tutar.toString(),
      cihaz_degisimi: proposal.cihaz_degisimi || false
    });
    setEditingId(proposal.id);
    setActiveTab("create");
    toast({
      title: "Düzenleme Modu",
      description: "Formu doldurup güncelleyin"
    });
  };

  const handleDelete = async (proposalId) => {
    if (!window.confirm('Bu teklifi silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      await axios.delete(`${API}/proposals/${proposalId}`);
      toast({
        title: "Başarılı!",
        description: "Teklif silindi"
      });
      fetchProposals();
    } catch (error) {
      console.error("Teklif silinemedi:", error);
      toast({
        title: "Hata",
        description: "Teklif silinemedi",
        variant: "destructive"
      });
    }
  };

  // Çeviri fonksiyonu
  const handleTranslate = async () => {
    if (!formData.kaza_detayi || !formData.kaza_detayi.trim()) {
      toast({
        title: "Uyarı",
        description: "İngilizce kaza detayı alanı boş",
        variant: "destructive"
      });
      return;
    }

    setTranslating(true);
    try {
      const response = await axios.post(`${API}/translate`, null, {
        params: { text: formData.kaza_detayi }
      });
      
      setFormData(prev => ({
        ...prev,
        kaza_detayi_tr: response.data.translated_text
      }));
      
      const provider = response.data?.provider || "Google Translate";
      const isDeepL = provider.includes("DeepL");

      toast({
        title: `Çeviri Tamamlandı (${provider})`,
        description: "Lütfen metni kontrol edip gerekirse düzenleyin",
        className: isDeepL 
          ? "bg-blue-600 text-white border-blue-700" 
          : "bg-emerald-600 text-white border-emerald-700",
        style: {
          backgroundColor: isDeepL ? "#2563eb" : "#16a34a",
          color: "#ffffff"
        }
      });
    } catch (error) {
      console.error("Çeviri hatası:", error);
      toast({
        title: "Hata",
        description: "Çeviri başarısız oldu",
        variant: "destructive"
      });
    } finally {
      setTranslating(false);
    }
  };

  // Mail metni kopyalama fonksiyonu
  const handleCopyMailText = (proposal) => {
    const mailText = `Merhaba\n\n${proposal.seri_numarasi} seri numaralı ve ${proposal.is_numarasi} iş numaralı ${proposal.model} ürünün kontrolleri tamamlanmış olup ekte teklif iletilmiştir.\nOlumlu olumsuz cevabınızı bekliyoruz.`;
    
    navigator.clipboard.writeText(mailText).then(() => {
      toast({
        title: "Kopyalandı!",
        description: "Mail metni panoya kopyalandı"
      });
    }).catch(() => {
      toast({
        title: "Hata",
        description: "Kopyalama başarısız oldu",
        variant: "destructive"
      });
    });
  };

  const handleOpenInOutlook = (proposal) => {
    const subject = `${proposal.is_numarasi} - ${proposal.model} Teklif`;
    const body = `Merhaba\n\n${proposal.seri_numarasi} seri numaralı ve ${proposal.is_numarasi} iş numaralı ${proposal.model} ürünün kontrolleri tamamlanmış olup ekte teklif iletilmiştir.\nOlumlu olumsuz cevabınızı bekliyoruz.`;
    const mailtoUrl = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailtoUrl;
  };
  const handleOpenWhatsApp = (proposal) => {
    const phone = window.prompt("Telefon numarasını girin (başında ülke kodu ile, örn: 905XXXXXXXXX):");
    if (!phone) return;
    const cleanPhone = phone.replace(/[^0-9]/g, '');
    const body = `Merhaba\n\n${proposal.seri_numarasi} seri numaralı ve ${proposal.is_numarasi} iş numaralı ${proposal.model} ürünün kontrolleri tamamlanmış olup ekte teklif iletilmiştir.\nOlumlu olumsuz cevabınızı bekliyoruz.`;
    const waUrl = `https://web.whatsapp.com/send?phone=${cleanPhone}&text=${encodeURIComponent(body)}`;
    window.open(waUrl, '_blank');
  };

  // Helper fonksiyonlar - Ek Açıklama alanına otomatik metin ekleme
  const handleUrunBeklenecekToggle = (checked) => {
    const text = "STOKTA ÜRÜN OLMADIĞINDAN 3-4 HAFTA KADAR ÜRÜN GELMESİ BEKLENECEKTİR";
    
    if (checked) {
      // Checkbox işaretlendiğinde metni ekle
      const currentText = formData.ek_aciklama;
      const newText = currentText ? `${currentText}\n\n${text}` : text;
      setFormData(prev => ({ ...prev, ek_aciklama: newText }));
    }
  };

  const handleDataIletilmediToggle = (checked) => {
    if (checked && formData.model) {
      const text = `${formData.model} ürününüzün dataları iletilmediğinden ücretli işlem uygulanacaktır.`;
      const currentText = formData.ek_aciklama;
      const newText = currentText ? `${currentText}\n\n${text}` : text;
      setFormData(prev => ({ ...prev, ek_aciklama: newText }));
    } else if (checked && !formData.model) {
      toast({
        title: "Uyarı",
        description: "Lütfen önce Model alanını doldurun",
        variant: "destructive"
      });
    }
  };



  const handleGorselSistemUyarisiToggle = (checked) => {
    if (checked) {
      const textEn = "Please note: The visual system cannot recognize surfaces without texture features, and it cannot operate normally in environments with insufficient or excessive lighting. In the following scenarios, the visual system will not function properly:\n1) Monochrome surfaces (such as pure black, pure white, pure red, pure green);\n2) Surfaces with strong reflection or mirror images (such as ice surfaces);\n3) Water surfaces or transparent object surfaces;\n4) Surfaces of moving objects (such as above crowds, or shrubs and grass swayed by strong winds);\n5) Scenes with rapid and drastic changes in lighting;\n6) Surfaces that are extremely dark (illumination less than 10 lux) or extremely bright (illumination greater than 40,000 lux);\n7) Surfaces made of materials that strongly absorb or reflect infrared (such as mirrors);\n8) Surfaces with very sparse texture;\n9) Surfaces with highly repetitive texture (such as tiles with identical colors);\n10) Small obstacles.";
      const currentEn = formData.kaza_detayi;
      const newEn = currentEn ? `${currentEn}\n\n${textEn}` : textEn;
      setFormData(prev => ({ ...prev, kaza_detayi: newEn }));
    }
  };
  const handleKazaVerisiBulunamadiToggle = (checked) => {
    if (checked) {
      const text = "Belirtilen tarihlerde kaza verisi bulunamadı.";
      const currentText = formData.ek_aciklama;
      const newText = currentText ? `${currentText}

${text}` : text;
      setFormData(prev => ({ ...prev, ek_aciklama: newText }));
    }
  };

  const handleCihazDegisimiToggle = (checked) => {
    if (checked && formData.model) {
      const text = `Aksesuarsız ${formData.model} cihaz teklif edilmektedir.`;
      const currentText = formData.degisecek_parcalar;
      const newText = currentText ? `${currentText}\n\n${text}` : text;
      setFormData(prev => ({ ...prev, degisecek_parcalar: newText, cihaz_degisimi: true }));
    } else if (checked && !formData.model) {
      toast({
        title: "Uyarı",
        description: "Lütfen önce Model alanını doldurun",
        variant: "destructive"
      });
    } else {
      setFormData(prev => ({ ...prev, cihaz_degisimi: false }));
    }
  };

  const cancelEdit = () => {
    setEditingId(null);
    setFormData({
      is_numarasi: "",
      seri_numarasi: "",
      model: "",
      musteri_sikayeti: "",
      degisecek_parcalar: "",
      ederini_asar: false,
      ek_aciklama: "",
      kaza_detayi: "",
      ingilizce_gozuksun: true,
      kaza_detayi_tr: "",
      kullanici_hatasi: false,
      toplam_tutar: "",
      cihaz_degisimi: false
    });
  };

  if (!authenticated) {
    return <LoginScreen onLogin={() => { sessionStorage.setItem('karfo_auth', 'true'); setAuthenticated(true); }} />;
  }

  if (!authenticated) {
    return <LoginScreen onLogin={() => { sessionStorage.setItem('karfo_auth', 'true'); setAuthenticated(true); }} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">📄 Teklif Hazırlama Sistemi</h1>
          <p className="text-gray-600">Teknik Servis Takip Sistemi</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-6">
            <TabsTrigger value="create" data-testid="create-tab">
              <Plus className="w-4 h-4 mr-2" />
              Yeni Teklif
            </TabsTrigger>
            <TabsTrigger value="list" data-testid="list-tab">
              <FileText className="w-4 h-4 mr-2" />
              Tüm Teklifler ({proposals.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="create">
            <Card>
              <CardHeader>
                <CardTitle data-testid="form-title">
                  {editingId ? "Teklif Düzenle" : "Yeni Teklif Oluştur"}
                </CardTitle>
                <CardDescription>
                  Müşteriye göndermek için teklif bilgilerini doldurun
                </CardDescription>
                <div className="pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleIsSayfasindanAyristir}
                    data-testid="is-sayfasindan-ayristir-button"
                  >
                    Ayrıştır
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="is_numarasi">İş Numarası *</Label>
                      <Input
                        id="is_numarasi"
                        name="is_numarasi"
                        value={formData.is_numarasi}
                        onChange={handleInputChange}
                        placeholder="Örn: IS-2024-001"
                        required
                        data-testid="is-numarasi-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="seri_numarasi">Seri Numarası *</Label>
                      <Input
                        id="seri_numarasi"
                        name="seri_numarasi"
                        value={formData.seri_numarasi}
                        onChange={handleInputChange}
                        placeholder="Örn: DJI123456789"
                        required
                        data-testid="seri-numarasi-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="model">Model *</Label>
                      <Input
                        id="model"
                        name="model"
                        value={formData.model}
                        onChange={handleInputChange}
                        placeholder="Örn: DJI Mavic 3 Pro"
                        required
                        data-testid="model-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="toplam_tutar">Toplam Tutar (TL) *</Label>
                      <Input
                        id="toplam_tutar"
                        name="toplam_tutar"
                        type="number"
                        step="0.01"
                        value={formData.toplam_tutar}
                        onChange={handleInputChange}
                        placeholder="Örn: 2500.00"
                        required
                        data-testid="toplam-tutar-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="musteri_sikayeti">Müşteri Şikayeti (Opsiyonel)</Label>
                    <Textarea
                      id="musteri_sikayeti"
                      name="musteri_sikayeti"
                      value={formData.musteri_sikayeti}
                      onChange={handleInputChange}
                      placeholder="Örn: Cihaz çalışmıyor, açılmıyor"
                      rows={2}
                      data-testid="musteri-sikayeti-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="degisecek_parcalar">Değişecek Parçalar *</Label>
                    <Textarea
                      id="degisecek_parcalar"
                      name="degisecek_parcalar"
                      value={formData.degisecek_parcalar}
                      onChange={handleInputChange}
                      placeholder="Örn: Kamera gimbal motoru, ana kart değişimi, batarya değişimi"
                      rows={3}
                      required
                      data-testid="degisecek-parcalar-input"
                    />
                  </div>

                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="ederini_asar"
                      name="ederini_asar"
                      checked={formData.ederini_asar}
                      onCheckedChange={(checked) => {
                        setFormData(prev => ({ ...prev, ederini_asar: checked }));
                      }}
                      data-testid="ederini-asar-checkbox"
                    />
                    <Label 
                      htmlFor="ederini_asar" 
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                    >
                      Ederini Aşar
                    </Label>
                  </div>
		<div className="flex items-center space-x-2">
                    <Checkbox
                      id="tamiri_mumkun_degil"
                      checked={formData.tamiri_mumkun_degil}
                      onCheckedChange={(checked) =>
                        setFormData({ ...formData, tamiri_mumkun_degil: checked })
                      }
                    />
                    <Label
                      htmlFor="tamiri_mumkun_degil"
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                      Tamiri Mümkün Değil
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="cihaz_degisimi"
                      checked={formData.cihaz_degisimi}
                      onCheckedChange={handleCihazDegisimiToggle}
                    />
                    <Label
                      htmlFor="cihaz_degisimi"
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                      Cihaz Değişimi
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="kaza_verisi_bulunamadi"
                      onCheckedChange={handleKazaVerisiBulunamadiToggle}
                    />
                    <Label
                      htmlFor="kaza_verisi_bulunamadi"
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                      Kaza Verisi Bulunamadı
                    </Label>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="ek_aciklama">Ek Açıklama (Opsiyonel)</Label>
                    <Textarea
                      id="ek_aciklama"
                      name="ek_aciklama"
                      value={formData.ek_aciklama}
                      onChange={handleInputChange}
                      placeholder="Örn: Darbeli bataryanın tamiri mümkün olmadığından yeni batarya ile ücretli. Ücret onaylanması durumunda bir süre batarya gelmesi beklenecektir."
                      rows={3}
                      data-testid="ek-aciklama-input"
                    />
                  </div>

                  <div className="flex flex-wrap gap-4">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="urun_beklenecek"
                        name="urun_beklenecek"
                        onCheckedChange={handleUrunBeklenecekToggle}
                        data-testid="urun-beklenecek-checkbox"
                      />
                      <Label 
                        htmlFor="urun_beklenecek" 
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                      >
                        Parça/Ürün Beklenecek
                      </Label>
                    </div>

                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="data_iletilmedi"
                        name="data_iletilmedi"
                        onCheckedChange={handleDataIletilmediToggle}
                        data-testid="data-iletilmedi-checkbox"
                      />
                      <Label 
                        htmlFor="data_iletilmedi" 
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                      >
                        Data İletilmedi
                      </Label>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="kaza_detayi">Kaza Analiz Detayı - İngilizce (Opsiyonel)</Label>
                    <Textarea
                      id="kaza_detayi"
                      name="kaza_detayi"
                      value={formData.kaza_detayi}
                      onChange={handleInputChange}
                      placeholder="Her satıra bir madde yazın. Otomatik numaralanacaktır.&#10;Örn:&#10;The aircraft worked under Sport mode after took off&#10;Flight Time T=566.1s, Relative Height H=-2.7m&#10;The incident coordinate:(38.6792232,39.1669527)"
                      rows={4}
                      data-testid="kaza-detayi-input"
                    />
                  </div>

                  <div className="flex flex-wrap gap-4">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="gorsel_sistem_uyarisi"
                        name="gorsel_sistem_uyarisi"
                        onCheckedChange={handleGorselSistemUyarisiToggle}
                        data-testid="gorsel-sistem-uyarisi-checkbox"
                      />
                      <Label
                        htmlFor="gorsel_sistem_uyarisi"
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer flex items-center gap-1"
                      >
                        <span aria-hidden="true">↑</span> Görsel Sistem Uyarısı
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="ingilizce_gozuksun"
                        name="ingilizce_gozuksun"
                        checked={formData.ingilizce_gozuksun}
                        onCheckedChange={(checked) => {
                          setFormData(prev => ({ ...prev, ingilizce_gozuksun: checked }));
                        }}
                        data-testid="ingilizce-gozuksun-checkbox"
                      />
                      <Label 
                        htmlFor="ingilizce_gozuksun" 
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                      >
                        Teklifte Görünsün
                      </Label>
                    </div>

                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="kullanici_hatasi"
                        name="kullanici_hatasi"
                        checked={formData.kullanici_hatasi}
                        onCheckedChange={(checked) => {
                          setFormData(prev => ({ ...prev, kullanici_hatasi: checked }));
                        }}
                        data-testid="kullanici-hatasi-checkbox"
                      />
                      <Label 
                        htmlFor="kullanici_hatasi" 
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                      >
                        Kullanıcı Hatası
                      </Label>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="kaza_detayi_tr">Kaza Analiz Detayı - Türkçe (Opsiyonel)</Label>
                      <Button
                        type="button"
                        onClick={handleTranslate}
                        variant="outline"
                        size="sm"
                        disabled={loading || translating || !formData.kaza_detayi}
                        data-testid="translate-button"
                      >
                        {translating ? "⏳ Çeviri deneniyor..." : "🌐 Çeviri"}
                      </Button>
                    </div>
                    <Textarea
                      id="kaza_detayi_tr"
                      name="kaza_detayi_tr"
                      value={formData.kaza_detayi_tr}
                      onChange={handleInputChange}
                      placeholder="Her satıra bir madde yazın. Otomatik numaralanacaktır.&#10;Örn:&#10;Hava aracı kalkış yaptıktan sonra Spor modunda çalışıyordu&#10;Uçuş Süresi T=566.1s, Bağıl Yükseklik H=-2.7m&#10;Olay koordinatı:(38.6792232,39.1669527)"
                      rows={4}
                      data-testid="kaza-detayi-tr-input"
                    />
                  </div>

                  <div className="flex gap-2">
                    <Button 
                      type="submit" 
                      disabled={loading}
                      className="flex-1"
                      data-testid="submit-button"
                    >
                      {loading ? "İşleniyor..." : editingId ? "Güncelle" : "Teklif Oluştur"}
                    </Button>
                    {editingId && (
                      <Button 
                        type="button" 
                        variant="outline" 
                        onClick={cancelEdit}
                        data-testid="cancel-edit-button"
                      >
                        İptal
                      </Button>
                    )}
                  </div>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="list">
            {/* Aylık İstatistikler */}
            {monthlyStats && (
              <div className="mb-4 bg-white border rounded-lg p-4 shadow-sm">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-gray-700">
                    {monthlyStats.month} {monthlyStats.year}
                  </h3>
                  <span className="text-xs text-gray-500">
                    Toplam: {monthlyStats.total_count} teklif
                  </span>
                  {monthlyStats.earliest_date && (() => {
                    const d = new Date(monthlyStats.earliest_date);
                    const dd = String(d.getDate()).padStart(2, '0');
                    const mm = String(d.getMonth() + 1).padStart(2, '0');
                    const yyyy = d.getFullYear();
                    return (
                      <span className="text-xs text-gray-400 ml-2">
                        ({dd}/{mm}/{yyyy}'dan bugüne {monthlyStats.all_time_total} kayıt)
                      </span>
                    );
                  })()}
                </div>
                <TooltipProvider>
                  <div className="flex gap-1 overflow-x-auto pb-2">
                    {monthlyStats.daily_stats.map((stat) => {
                      const isSelected = selectedDate && selectedDate.day === stat.day;
                      const monthNum = ["", "Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"].indexOf(monthlyStats.month);
                      
                      return (
                        <Tooltip key={stat.day} delayDuration={200}>
                          <TooltipTrigger asChild>
                            <button
                              onClick={() => handleDayClick(stat.day, monthNum, monthlyStats.year)}
                              className="flex flex-col items-center min-w-[32px] cursor-pointer focus:outline-none"
                            >
                              <div 
                                className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium transition-all hover:scale-110 ${
                                  isSelected
                                    ? 'bg-green-600 text-white ring-2 ring-green-400'
                                    : stat.is_today 
                                    ? 'bg-blue-600 text-white' 
                                    : stat.count > 0 
                                    ? 'bg-blue-100 text-blue-700 hover:bg-blue-200' 
                                    : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                                }`}
                              >
                                {stat.day}
                              </div>
                            </button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="font-semibold">{stat.day} {monthlyStats.month} {monthlyStats.year}</p>
                            <p className="text-sm">{stat.count} teklif oluşturuldu</p>
                            {stat.is_today && <p className="text-xs text-blue-500 mt-1">📅 Bugün</p>}
                            {stat.count > 0 && <p className="text-xs text-gray-400 mt-1">🔍 Tıkla ve filtrele</p>}
                          </TooltipContent>
                        </Tooltip>
                      );
                    })}
                  </div>
                </TooltipProvider>
                
                {/* Aktif Filtre Göstergesi */}
                {selectedDate && (
                  <div className="mt-3 flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-blue-700">
                        📅 {selectedDate.day} {monthlyStats.month} {selectedDate.year}
                      </span>
                      <span className="text-xs text-blue-600">
                        ({filteredProposals.length} teklif)
                      </span>
                    </div>
                    <Button 
                      onClick={clearFilter}
                      variant="ghost" 
                      size="sm"
                      className="text-blue-600 hover:text-blue-800 hover:bg-blue-100"
                    >
                      ✕ Filtreyi Kaldır
                    </Button>
                  </div>
                )}
              </div>
            )}

            <div className="space-y-4">
              {filteredProposals.length === 0 && !selectedDate && proposals.length === 0 ? (
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center py-12">
                      <FileText className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">Henüz teklif yok</h3>
                      <p className="text-gray-600 mb-4">İlk teklifinizi oluşturmak için &quot;Yeni Teklif&quot; sekmesine gidin</p>
                    </div>
                  </CardContent>
                </Card>
              ) : filteredProposals.length === 0 && selectedDate ? (
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center py-12">
                      <FileText className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">Bu tarihte teklif yok</h3>
                      <p className="text-gray-600 mb-4">{selectedDate.day} {monthlyStats?.month} {selectedDate.year} tarihinde oluşturulmuş teklif bulunamadı</p>
                      <Button onClick={clearFilter} variant="outline">Tüm Teklifleri Göster</Button>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                filteredProposals.slice((currentPage - 1) * PROPOSALS_PER_PAGE, currentPage * PROPOSALS_PER_PAGE).map((proposal) => (
                  <Card key={proposal.id} data-testid={`proposal-card-${proposal.id}`}>
                    <CardHeader>
                      <div className="flex justify-between items-start">
                        <div>
                          <CardTitle className="text-xl">
                            İş No: {proposal.is_numarasi}
                          </CardTitle>
                          <CardDescription>
                            Oluşturulma: {new Date(proposal.created_at).toLocaleString('tr-TR')}
                          </CardDescription>
                        </div>
                        <div className="text-2xl font-bold text-green-600">
                          {proposal.toplam_tutar.toFixed(2)} TL
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-gray-600">Seri Numarası</p>
                          <p className="font-semibold">{proposal.seri_numarasi}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Model</p>
                          <p className="font-semibold">{proposal.model}</p>
                        </div>
                        {proposal.musteri_sikayeti && proposal.musteri_sikayeti.trim() && (
                          <div className="md:col-span-2">
                            <p className="text-sm text-gray-600">Müşteri Şikayeti</p>
                            <p className="text-gray-800">{proposal.musteri_sikayeti}</p>
                          </div>
                        )}
                        <div className="md:col-span-2">
                          <p className="text-sm text-gray-600">Değişecek Parçalar</p>
                          <p className="text-gray-800">{proposal.degisecek_parcalar}</p>
                        </div>
                        {proposal.kaza_detayi && proposal.kaza_detayi.trim() && (
                          <div className="md:col-span-2">
                            <p className="text-sm text-gray-600">Kaza Analiz Detayı (İngilizce)</p>
                            <p className="text-gray-800 text-xs whitespace-pre-line">{proposal.kaza_detayi}</p>
                          </div>
                        )}
                        {proposal.kaza_detayi_tr && proposal.kaza_detayi_tr.trim() && (
                          <div className="md:col-span-2">
                            <p className="text-sm text-gray-600">Kaza Analiz Detayı (Türkçe)</p>
                            <p className="text-gray-800 text-xs whitespace-pre-line">{proposal.kaza_detayi_tr}</p>
                          </div>
                        )}
                      </div>

                      {/* Mail Metni Bölümü */}
                      <div className="mt-6 pt-4 border-t">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-sm font-semibold text-gray-700">Mail Metni</p>
                          <div className="flex items-center gap-2">
                            <Button
                              onClick={() => handleOpenWhatsApp(proposal)}
                              variant="outline"
                              size="sm"
                              data-testid={`open-whatsapp-${proposal.id}`}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.372-.01-.571-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
                                <path d="M12.014 2C6.478 2 2 6.478 2 12.014c0 1.905.532 3.756 1.54 5.36L2 22l4.762-1.516a9.94 9.94 0 004.796 1.22h.005c5.536 0 10.014-4.478 10.014-10.014C21.577 6.478 17.55 2 12.014 2zm0 18.16h-.004a8.14 8.14 0 01-4.148-1.135l-.297-.176-3.075.98.98-3.005-.194-.308a8.135 8.135 0 01-1.253-4.337c0-4.51 3.674-8.184 8.19-8.184 2.19 0 4.245.852 5.79 2.399a8.13 8.13 0 012.397 5.79c0 4.511-3.673 8.186-8.186 8.186z" />
                              </svg>
                              WhatsApp'ta Aç
                            </Button>
                            <Button
                              onClick={() => handleOpenInOutlook(proposal)}
                              variant="outline"
                              size="sm"
                              data-testid={`open-outlook-${proposal.id}`}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                              </svg>
                              Outlook'ta Aç
                            </Button>
                            <Button
                              onClick={() => handleCopyMailText(proposal)}
                              variant="outline"
                              size="sm"
                              data-testid={`copy-mail-${proposal.id}`}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                              </svg>
                              Kopyala
                            </Button>
                          </div>
                        </div>
                        <div className="bg-gray-50 p-3 rounded-md text-sm text-gray-700 whitespace-pre-line border border-gray-200">
                          {`Merhaba\n\n${proposal.seri_numarasi} seri numaralı ve ${proposal.is_numarasi} iş numaralı ${proposal.model} ürünün kontrolleri tamamlanmış olup ekte teklif iletilmiştir.\nOlumlu olumsuz cevabınızı bekliyoruz.`}
                        </div>
                      </div>
                    </CardContent>
                    <CardFooter className="flex gap-2">
                      <Button
                        onClick={() => handleDownloadPDF(proposal.id, proposal.is_numarasi)}
                        variant="default"
                        className="flex-1"
                        data-testid={`download-pdf-${proposal.id}`}
                      >
                        <Download className="w-4 h-4 mr-2" />
                        PDF İndir
                      </Button>
                      <Button
                        onClick={() => handleEdit(proposal)}
                        variant="outline"
                        data-testid={`edit-button-${proposal.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        onClick={() => handleDelete(proposal.id)}
                        variant="destructive"
                        data-testid={`delete-button-${proposal.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </CardFooter>
                  </Card>
                ))
              )}
              {filteredProposals.length > PROPOSALS_PER_PAGE && (
                <div className="flex items-center justify-center gap-4 pt-4">
                  <Button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    variant="outline"
                    size="sm"
                    data-testid="prev-page-button"
                  >
                    Önceki
                  </Button>
                  <span className="text-sm text-gray-600">
                    Sayfa {currentPage} / {Math.ceil(filteredProposals.length / PROPOSALS_PER_PAGE)}
                  </span>
                  <Button
                    onClick={() => setCurrentPage(p => Math.min(Math.ceil(filteredProposals.length / PROPOSALS_PER_PAGE), p + 1))}
                    disabled={currentPage >= Math.ceil(filteredProposals.length / PROPOSALS_PER_PAGE)}
                    variant="outline"
                    size="sm"
                    data-testid="next-page-button"
                  >
                    Sonraki
                  </Button>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
      <Toaster />
    </div>
  );
}

export default App;
